import events from "@/bl-events/events-singleton"

// Interface between the global events of this actual app and those of the legacy one.
let $ = window.jQuery

/**
 * If your module requires some global listeners for events from the event bus
 * (especially "abtest:ready" or "setup:ready") it is best practice to not register
 * for these events in your module itself.
 * Rather export an event handler object with event-names as keys and the
 * corresponding event handler function as the value.
 * Then use `registerEventHandlers` in your pack file to hook your module up to
 * the event bus.
 *
 * The advantages of this are:
 * - No global state in your module.
 * - It is easier to unit test what your module does when a certain event happens
 *    because you do not rely on sending the event through the bus, but can directly
 *    invoke the exposed function.
 * - It avoids side effects caused by dangling event handlers that listen to the same
 *    event you push into the event bus for testing.
 * - It is very clear in a pack file which modules have a global dependency on the
 *    event bus.
 *
 * **Example:**
 *
 * Module file:
 * ```
 * export const eventHandlers = {
 *    "abtest:ready": () => {
 *      // do what needs to be done on received the event "abtest:ready" from the event bus
 *    },
 *    "setup:ready": () => {
 *      // do what needs to be done on received the event "setup:ready" from the event bus
 *    },
 *    "my-event": () => {
 *      // do what needs to be done on received the event "my-event" from the event bus
 *    }
 * }
 * ...
 * ```
 *
 * Pack file:
 * ```
 * import {registerEventHandler} from "@/bl-events";
 * import {eventHandlers as abcEventHandlers} from "/your-module-abc"
 * import {eventHandlers as xyzEventHandlers} from "/your-module-xyz"
 *
 * registerEventHandler([abcEventHandlers, xyzEventHandlers]);
 * ```
 */
export const registerEventHandlers = (modules = {}) => {
  modules.forEach((module) => {
    Object.keys(module).forEach((keyEvent) => {
      const handler = module[keyEvent]
      events.on(keyEvent, (...args) => {
        if (handler && typeof handler === "function") {
          handler(...args)
        }
      })
    })
  })
}

export default {
  on: (name, next) => {
    events.on(name, next)
    if ($) {
      $(document).on(`blinkist:${name}`, (event, ...payload) => {
        next(...payload)
      })
    }
  },
  once: (name, next) => {
    events.once(name, next)
    if ($) {
      $(document).one(`blinkist:${name}`, (event, ...payload) => {
        next(...payload)
      })
    }
  },
  emit: (name, ...payload) => {
    events.emit(name, ...payload)
    // Prefix event name to not trigger event twice
    if ($) {
      $(document).trigger(`blinkist:${name}`, payload)
    }
  },
  track: (...payload) => {
    if (window._bk_paq) {
      window._bk_paq.trackEvent(payload)
    } else {
      events.emit("trackEvent", payload)
    }
  },
  // Emit an event without transmitting it to legacy code
  // Prevent some events to be called twice
  emitNoLegacy: (name, ...payload) => {
    events.emit(name, ...payload)
  }
}
